/**
 * Per-design check backs — resolver, security-panel wording map, and the
 * single engine-config builder used by BOTH back call sites (the
 * on-screen CheckBack component and the PNG/PDF export StaticCanvas).
 *
 * Built from the 2026-11-03 check-back research spec
 * (~/workspace/omnicheck-studio-work/check-back-research-2026-11-03.md):
 * real US check backs vary along four honest axes only — the deposit
 * block default, the security panel's CONTENT (the features the design
 * actually has), the visual family (tint / frame / watermark), and a
 * few type-specific elements (money order, U.S. Treasury, printed
 * eCheck). Endorsement-zone furniture never varies, and the owner's
 * endorsement-line geometry is not touched anywhere in this system.
 *
 * Panel honesty rule: a panel line prints ONLY when it comes from a
 * real source — a front security feature that is actually enabled, or
 * a string in the bank template's own specs.securityFeatures. Since
 * the 2026-11-04 per-design security identity, wordings that name a
 * front feature additionally require that feature to be enabled (see
 * followEnabledSet below), so the write-up follows the face in every
 * panel mode. A design with nothing mappable prints an empty panel,
 * never a fabricated one.
 */

import { bankSpecificTemplates } from './bankTemplates';

export type BackDepositStyle = 'standard' | 'mobile' | 'atm' | 'business';
export type BackPanelMode = 'dynamic' | 'template' | 'reduced-dynamic' | 'reduced-template' | 'treasury' | 'none';

export interface ResolvedBackVariant {
    /** V1–V10 per the spec, plus V7L (UK lite) and VT (traveler's). */
    variant: string;
    variantName: string;
    depositBlockDefault: BackDepositStyle;
    panelMode: BackPanelMode;
    /** V3's extended panel may run up to 8 lines inside the same box. */
    panelExtended: boolean;
    footnote: 'regcc' | 'neutral' | 'none';
    /** null = no watermark (V4 plain temporary stock / V7 / V7L). */
    watermarkText: [string, string] | null;
    /** Explicit field fill; null = derive a pale mix from the design tint. */
    fieldFill: string | null;
    /** Explicit strip fill; null = the standard warm ivory #faf6ec. */
    stripFill: string | null;
    /** Explicit security-box frame colour; null = family default. */
    frameStroke: string | null;
    /** Inner-rule colour for two-tone / double-rule frames. */
    frameInner: string | null;
    /** Text that REPLACES the "RESERVED FOR FINANCIAL INSTITUTION USE*"
     *  column (money-order payee-only note, traveler's countersign note). */
    stripNote: string | null;
    /** V3: print matching numbers in light ink in the FI field. */
    imageMatch: boolean;
    /** Which numbers the ImageMatch line prints: 'routing-check'
     *  (routing + check, the studio default) or 'account-check'
     *  where the variant's real feature prints account + check
     *  (USAA's ImageMatch(R), research [S1]). */
    imageMatchContent: 'routing-check' | 'account-check';
    /** V7: an instructions box stands where the security panel would be. */
    instructions: boolean;
    /** V6: the first endorsement line renders as USAUSAUSA microtext. */
    treasuryMicroprint: boolean;
}

const BASE: Omit<ResolvedBackVariant, 'variant' | 'variantName' | 'depositBlockDefault' | 'panelMode'> = {
    panelExtended: false,
    footnote: 'regcc',
    watermarkText: ['ORIGINAL', 'DOCUMENT'],
    fieldFill: null,
    stripFill: null,
    frameStroke: null,
    frameInner: null,
    stripNote: null,
    imageMatch: false,
    imageMatchContent: 'routing-check',
    instructions: false,
    treasuryMicroprint: false
};

const V = (
    variant: string,
    variantName: string,
    depositBlockDefault: BackDepositStyle,
    panelMode: BackPanelMode,
    extra: Partial<ResolvedBackVariant> = {}
): ResolvedBackVariant => ({ ...BASE, variant, variantName, depositBlockDefault, panelMode, ...extra });

// ---------------------------------------------------------------------------
// §5.3 security-panel wording map — canonical order is the priority order.
// A line is sourced from front feature ids (dynamic panels) or from the
// bank template's own specs.securityFeatures strings (template panels).
// Matching is by exact string so the map is unit-testable.
// ---------------------------------------------------------------------------

interface PanelWording {
    line: string;
    front: string[];
    specs: string[];
}

export const PANEL_WORDING: PanelWording[] = [
    { line: '• FLUORESCENT FIBER TECHNOLOGY', front: ['uv_markers'], specs: ['uv-fibers', 'uv-reactive', 'uv-reactive-ink', 'fluorescent-fibers', 'blue-fluorescent-fibers'] },
    { line: '• CHEMICAL SENSITIVE PAPER STOCK', front: ['chemical_reactive'], specs: ['chemical-reactive', 'chemical-sensitive', 'chemical-sensitive-paper', 'chemical-reactive-paper', 'react-to-bleach'] },
    { line: '• MICROPRINTING ALONG LINES (MP)', front: ['microprint', 'microprint_sig'], specs: ['microprinting', 'microprint-signature', 'microprint-border', 'microprint-lines', 'basic-microprinting', 'micro-perforations', 'micro-text'] },
    { line: '• SECURITY SCREEN BACKDROP', front: ['void_pantograph'], specs: ['void-pantograph', 'void-pantograph-heavy', 'security-screentone'] },
    { line: '• ROUTING NUMBER VERIFICATION', front: ['micr_clear_band'], specs: [] },
    { line: '• AUTHENTIC DOCUMENT WATERMARK', front: ['watermark'], specs: ['watermark-seal', 'watermark', 'check-stock-watermark', 'maple-leaf-watermark', 'crown-watermark', 'watermark-lion', 'watermark-eagle', 'watermark-liberty'] },
    { line: '• HOLOGRAPHIC SECURITY ELEMENT', front: ['holographic'], specs: ['holographic-thread', 'square-hologram', 'hologram', 'holographic-foil', 'holographic-strip', 'holographic-band', 'holographic-eagle', 'dual-hologram', 'hologram-logo', 'holographic-stamp'] },
    { line: '• HEAT-REACTIVE (THERMOCHROMIC) INK', front: ['thermochromic'], specs: ['thermochromic-ink', 'heat-reactive', 'thermal-ink'] },
    { line: '• PRISMATIC SECURITY PATTERN', front: ['prismatic_column'], specs: ['prismatic'] },
    { line: '• SECURITY WEAVE PATTERN', front: [], specs: ['security-weave'] },
    { line: '• MARBLE CLOUD SECURITY TEXTURE', front: [], specs: ['marble-cloud-texture', 'marble-texture'] },
    { line: '• VERTICAL SECURITY BAR', front: [], specs: ['vertical-security-bar', 'vertical-bofa-security-strip'] },
    { line: '• CPSA PADLOCK ICON', front: ['cpsa_padlock'], specs: ['secure-padlock', 'lock-icon-security'] },
    { line: '• RAINBOW SECURITY BORDER', front: [], specs: ['rainbow-border', 'rainbow-printing'] }
];

/** Panel lines for a dynamic panel: only features actually enabled. */
export function mapPanelLinesFromFeatures(enabledFeatureIds: string[], cap = 6): string[] {
    const enabled = new Set(enabledFeatureIds);
    return PANEL_WORDING.filter(w => w.front.some(id => enabled.has(id))).map(w => w.line).slice(0, cap);
}

/** Panel lines for a template panel: only the template's own spec strings. */
export function mapPanelLinesFromTemplateSpecs(specFeatures: string[], cap = 6): string[] {
    const specs = new Set(specFeatures);
    return PANEL_WORDING.filter(w => w.specs.some(s => specs.has(s))).map(w => w.line).slice(0, cap);
}

const LINE = (text: string) => PANEL_WORDING.find(w => w.line === text)!;

/** V4's reduced panel: screen, microprint, chemical — in that order,
 *  still sourced only from features/specs the design really has. */
const REDUCED_ORDER = [
    LINE('• SECURITY SCREEN BACKDROP'),
    LINE('• MICROPRINTING ALONG LINES (MP)'),
    LINE('• CHEMICAL SENSITIVE PAPER STOCK')
];

export function mapReducedPanelFromFeatures(enabledFeatureIds: string[]): string[] {
    const enabled = new Set(enabledFeatureIds);
    return REDUCED_ORDER.filter(w => w.front.some(id => enabled.has(id))).map(w => w.line);
}

export function mapReducedPanelFromTemplateSpecs(specFeatures: string[]): string[] {
    const specs = new Set(specFeatures);
    return REDUCED_ORDER.filter(w => w.specs.some(s => specs.has(s))).map(w => w.line);
}

/** V5's reduced issuer list: watermark, serial verification, microprint.
 *  The serial line exists only when the template's own specs claim
 *  sequential numbering — it is never invented for the style alone. */
export function mapMoneyOrderPanelFromFeatures(enabledFeatureIds: string[]): string[] {
    const enabled = new Set(enabledFeatureIds);
    const out: string[] = [];
    if (enabled.has('watermark')) out.push('• AUTHENTIC DOCUMENT WATERMARK');
    if (enabled.has('microprint') || enabled.has('microprint_sig')) out.push('• MICROPRINTING ALONG LINES (MP)');
    return out;
}

export function mapMoneyOrderPanelFromTemplateSpecs(specFeatures: string[]): string[] {
    const specs = new Set(specFeatures);
    const out: string[] = [];
    if (PANEL_WORDING[5].specs.some(s => specs.has(s))) out.push('• AUTHENTIC DOCUMENT WATERMARK');
    if (specs.has('sequential-numbering')) out.push('• SERIAL NUMBER VERIFICATION');
    if (PANEL_WORDING[2].specs.some(s => specs.has(s))) out.push('• MICROPRINTING ALONG LINES (MP)');
    return out;
}

/** V6's treasury note box — the BFS treatment (watermark, USA microprint
 *  endorsement line, UV features) as notes, replacing the commercial
 *  feature list. See the spec's honest-limits section: these name the
 *  design treatment, they are not a claim about any printed copy. */
export const TREASURY_NOTE_LINES = [
    '• U.S. TREASURY WATERMARK',
    '• USA MICROPRINT ENDORSEMENT LINE',
    '• ULTRAVIOLET INK SECURITY FEATURES'
];

// ---------------------------------------------------------------------------
// §6.1 — the 13 check styles
// ---------------------------------------------------------------------------

const STYLE_BACK: Record<string, ResolvedBackVariant> = {
    cashier: V('V3', 'Bank-Issued High Security', 'standard', 'dynamic', {
        panelExtended: true, fieldFill: '#faf5e4', frameInner: '#888888', imageMatch: true
    }),
    personal: V('V1', 'Classic Personal', 'mobile', 'dynamic'),
    business: V('V2', 'Corporate Deposit', 'business', 'dynamic'),
    payroll: V('V2', 'Corporate Deposit', 'business', 'dynamic', { fieldFill: '#eef1f4' }),
    payroll_classic: V('V2', 'Corporate Deposit', 'business', 'dynamic', { fieldFill: '#f7efdd' }),
    payroll_karen: V('V2', 'Corporate Deposit', 'business', 'dynamic', { fieldFill: '#f6ecd9' }),
    echeck: V('V7', 'Printed eCheck', 'standard', 'none', {
        footnote: 'none', watermarkText: null, fieldFill: '#ffffff', stripFill: '#ffffff', instructions: true
    }),
    money_order: V('V5', 'Money Order', 'standard', 'reduced-dynamic', {
        fieldFill: '#f3f6e3', stripNote: 'PAYEE ENDORSEMENT ONLY'
    }),
    certified: V('V1', 'Classic Personal', 'standard', 'dynamic'),
    // V4 temporary stock carries NO field watermark (plain bank-issued
    // stock, minimal security printing) — watermarkText must be null
    // here or the BASE default (ORIGINAL / DOCUMENT) prints.
    counter: V('V4', 'Temporary / Counter', 'standard', 'reduced-dynamic', { fieldFill: '#fdfdfa', watermarkText: null }),
    bofa_classic: V('V8', 'BofA Classic Marble', 'mobile', 'template', {
        fieldFill: '#eef2f7', frameStroke: '#012169'
    }),
    bofa_v2: V('V9', 'BofA Business', 'business', 'template', {
        fieldFill: '#ffffff', frameStroke: '#012169', frameInner: '#da291c'
    }),
    us_treasury: V('V6', 'U.S. Treasury', 'standard', 'treasury', {
        watermarkText: ['U.S.', 'TREASURY'], fieldFill: '#eef4e6', stripFill: '#f2f5e6', treasuryMicroprint: true
    })
};

// ---------------------------------------------------------------------------
// §6.2 — bank templates. BOFA/BOFA_CLASSIC/BOFA_BUSINESS_V2/
// US_TREASURY_OFFICIAL resolve to the V9/V8/V9/V6 treatments; the rest
// inherit V10-style (tint/frame from the template's colours, panel from
// its own specs) with the deposit default from their category.
// ---------------------------------------------------------------------------

const TREASURY_BACK = (): ResolvedBackVariant => V('V6', 'U.S. Treasury', 'standard', 'treasury', {
    watermarkText: ['U.S.', 'TREASURY'], fieldFill: '#eef4e6', stripFill: '#f2f5e6', treasuryMicroprint: true
});

const BANK_BACK: Record<string, ResolvedBackVariant> = {
    BOFA: V('V9', 'BofA Business', 'business', 'template', {
        fieldFill: '#ffffff', frameStroke: '#012169', frameInner: '#da291c'
    }),
    BOFA_CLASSIC: V('V8', 'BofA Classic Marble', 'mobile', 'template', {
        fieldFill: '#eef2f7', frameStroke: '#012169'
    }),
    BOFA_BUSINESS_V2: V('V9', 'BofA Business', 'business', 'template', {
        fieldFill: '#ffffff', frameStroke: '#012169', frameInner: '#da291c'
    }),
    US_TREASURY_OFFICIAL: TREASURY_BACK(),
    // Cashier's template: the V3 high-security treatment; §6.2 files the
    // CASHIER template under business-issued, so its deposit default is
    // business (the cashier STYLE keeps the standard block, §6.1).
    CASHIER: V('V3', 'Bank-Issued High Security', 'business', 'template', {
        panelExtended: true, fieldFill: '#faf5e4', frameInner: '#888888', imageMatch: true
    }),
    ECHECK: V('V7', 'Printed eCheck', 'standard', 'none', {
        footnote: 'none', watermarkText: null, fieldFill: '#ffffff', stripFill: '#ffffff', instructions: true
    }),
    MONEY_ORDER: V('V5', 'Money Order', 'standard', 'reduced-template', {
        fieldFill: '#f3f6e3', stripNote: 'PAYEE ENDORSEMENT ONLY'
    }),
    COUNTER_CHECK: V('V4', 'Temporary / Counter', 'standard', 'reduced-template', { fieldFill: '#fdfdfa', watermarkText: null }),
    CERTIFIED: V('V1', 'Classic Personal', 'standard', 'template'),
    // USAA's real back (check-backs-specimen-research-2026-11-04.md
    // [S1]) prints ImageMatch(R): matching account and check numbers
    // on the back — so USAA resolves explicitly with imageMatch on,
    // on the same V10 mobile/template composition as its family. It
    // is therefore NOT in BANK_MOBILE below (the family loop would
    // overwrite this entry with the plain V10 resolution). The line's
    // content is account + check (imageMatchContent 'account-check'),
    // as the real feature prints — the earlier routing + check
    // substitution is RESOLVED (Princewill, 2026-11-04).
    USAA: V('V10', 'Bank Design', 'mobile', 'template', { imageMatch: true, imageMatchContent: 'account-check' }),
    TRAVELERS_CHECK: V('VT', "Traveler's Check", 'standard', 'template', {
        stripNote: 'COUNTERSIGNATURE ON FRONT'
    }),
    CANADIAN: V('V10', 'Canadian (CPA practice)', 'standard', 'template', { footnote: 'neutral' }),
    UK_STYLE: V('V7L', 'UK Style (plain back)', 'standard', 'none', {
        footnote: 'none', watermarkText: null
    }),
    KAREN_ART_STUDIO_PAYROLL: V('V2', 'Corporate Deposit', 'business', 'template', { fieldFill: '#f6ecd9' })
};

const BANK_MOBILE: string[] = [
    // Retail banks (§6.2)
    'CHASE', 'CHASE_PRIVATE', 'WELLS', 'CITI', 'PNC', 'TD', 'CAPITAL_ONE', 'US_BANK',
    'HUNTINGTON', 'FIFTH_THIRD', 'KEYBANK', 'CITIZENS', 'ALLY', 'DISCOVER', 'REGIONS',
    'TRUIST', 'BMO', 'ZIONS', 'COMERICA', 'FIRST_HORIZON', 'M_AND_T',
    // Credit unions / military (§6.2) — GOLDEN1 is a credit union, same family
    // (USAA resolves explicitly in BANK_BACK above — its real back
    // carries ImageMatch — so it is not in this family list.)
    'NAVY_FCU', 'PENFED', 'BECU', 'ALLIANT', 'GOLDEN1',
    // 2026-11-04 specimen designs - standard bank back family
    'GREENFI', 'ANB',
    // Card-issuer premium bank template — retail family
    'AMEX_CENTURION'
];

const BANK_BUSINESS: string[] = ['CONTRACTOR', 'VOUCHER', 'INDUSTRIAL_PAYROLL', 'PAYROLL'];
const BANK_STANDARD: string[] = ['GENERIC', 'CLASSIC_BLUE', 'CLASSIC_GREEN', 'CLASSIC_MAROON', 'PARCHMENT', 'MARBLE'];

for (const id of BANK_MOBILE) BANK_BACK[id] = V('V10', 'Bank Design', 'mobile', 'template');
for (const id of BANK_BUSINESS) BANK_BACK[id] = V('V10', 'Bank Design', 'business', 'template');
for (const id of BANK_STANDARD) BANK_BACK[id] = V('V10', 'Bank Design', 'standard', 'template');

// ---------------------------------------------------------------------------
// §6.2 — premium templates (19): V10 inheritance, dynamic panel from the
// front's enabled features, deposit default per the spec's list.
// ---------------------------------------------------------------------------

const PREMIUM_MOBILE: string[] = ['chime_pro', 'varo_style', 'credit_union_classic'];
const PREMIUM_BUSINESS: string[] = [
    'classic_elegant', 'executive_gold', 'royal_blue', 'corporate_grey', 'emerald_prestige',
    'burgundy_classic', 'midnight_premium', 'bronze_heritage', 'ultra_obsidian', 'ultra_sapphire',
    'ultra_amethyst', 'ultra_crimson', 'ultra_diamond', 'corporate_safety', 'prepaid_gold'
];

function resolvePremiumBack(id: string): ResolvedBackVariant | null {
    if (id === 'us_treasury') return TREASURY_BACK();
    if (PREMIUM_MOBILE.includes(id)) return V('V10', 'Premium Design', 'mobile', 'dynamic');
    if (PREMIUM_BUSINESS.includes(id)) return V('V10', 'Premium Design', 'business', 'dynamic');
    return null;
}

// ---------------------------------------------------------------------------
// §6.3 — the resolver. A selected template (bank or premium) defines the
// design, so it wins over the check style; otherwise the style rules.
// ---------------------------------------------------------------------------

export function resolveBackVariant(checkStyle: string, selectedBank: string | null | undefined): ResolvedBackVariant {
    if (selectedBank) {
        const premium = resolvePremiumBack(selectedBank);
        if (premium) return premium;
        if (BANK_BACK[selectedBank]) return BANK_BACK[selectedBank];
        if ((bankSpecificTemplates as any)[selectedBank]) {
            // Any future bank template defaults to the V10 inheritance.
            return V('V10', 'Bank Design', 'mobile', 'template');
        }
    }
    return STYLE_BACK[checkStyle] || V('V1', 'Classic Personal', 'mobile', 'dynamic');
}

/** The template spec strings a template panel is built from. For the
 *  BofA front STYLES the panel comes from the matching bank template's
 *  specs (spec §6.1) even when no template is selected. */
export function templateSpecFeaturesFor(checkStyle: string, selectedBank: string | null | undefined): string[] | null {
    if (selectedBank && (bankSpecificTemplates as any)[selectedBank]) {
        return (bankSpecificTemplates as any)[selectedBank].specs?.securityFeatures || [];
    }
    if (checkStyle === 'bofa_classic') return bankSpecificTemplates.BOFA_CLASSIC.specs.securityFeatures;
    if (checkStyle === 'bofa_v2') return bankSpecificTemplates.BOFA_BUSINESS_V2.specs.securityFeatures;
    return null;
}

/**
 * Per-design security identity (2026-11-04): a template panel's
 * line for a FRONT-BACKED wording (one with real front feature ids)
 * prints only when that feature is actually enabled on the face —
 * the panel follows the enabled set in every mode, not just the
 * dynamic ones. Specs-only wordings (front: [] — marble cloud
 * texture, vertical security bar, rainbow border, security weave)
 * name the template's own artwork treatments and keep their
 * spec source. Non-wording lines (the money-order serial line) are
 * untouched: their own spec rule already governs them.
 */
function followEnabledSet(lines: string[], enabledFeatureIds: string[]): string[] {
    const enabled = new Set(enabledFeatureIds);
    return lines.filter(line => {
        const wording = PANEL_WORDING.find(w => w.line === line);
        if (!wording || wording.front.length === 0) return true;
        return wording.front.some(id => enabled.has(id));
    });
}

/** Panel lines for a resolved variant, from the design's real sources. */
export function panelLinesFor(
    resolved: ResolvedBackVariant,
    enabledFeatureIds: string[],
    templateSpecFeatures: string[] | null
): string[] {
    const cap = resolved.panelExtended ? 8 : 6;
    switch (resolved.panelMode) {
        case 'dynamic':
            return mapPanelLinesFromFeatures(enabledFeatureIds, cap);
        case 'template':
            // Filter BEFORE the cap, so an enabled line is never
            // crowded out by a disabled one ahead of it.
            return followEnabledSet(mapPanelLinesFromTemplateSpecs(templateSpecFeatures || [], 99), enabledFeatureIds).slice(0, cap);
        case 'reduced-dynamic':
            return resolved.variant === 'V5'
                ? mapMoneyOrderPanelFromFeatures(enabledFeatureIds)
                : mapReducedPanelFromFeatures(enabledFeatureIds);
        case 'reduced-template':
            return resolved.variant === 'V5'
                ? followEnabledSet(mapMoneyOrderPanelFromTemplateSpecs(templateSpecFeatures || []), enabledFeatureIds)
                : followEnabledSet(mapReducedPanelFromTemplateSpecs(templateSpecFeatures || []), enabledFeatureIds);
        case 'treasury':
            return [...TREASURY_NOTE_LINES];
        case 'none':
            return [];
        default:
            return [];
    }
}

// ---------------------------------------------------------------------------
// The one config builder both call sites use (spec C9 — identical
// objects on screen and in export, so the two backs can never drift).
// ---------------------------------------------------------------------------

/**
 * Per-design styles for the Chemical Wash Detection Box (Princewill,
 * 2026-11-04: "make sure they have different styles"). The wash box
 * is not one identical white box on every design: each back-variant
 * family carries its own treatment, harmonised with the family's own
 * frame/palette tokens. Every chemical-capable design resolves to
 * exactly one style; close families may share, across families the
 * boxes visibly differ. In every style the box stays a functional
 * wash-detection area — a clear blank field a verifier can watch.
 *
 *  - plain:  single-rule grey border, white field, label above.
 *  - header: a filled header band across the box top carries the
 *            label (business/corporate backs).
 *  - double: double-rule border (high-security + premium backs; the
 *            premium V10 rules take the design's own frame colours).
 *  - tinted: the field is a pale screen of the design's ink with a
 *            matching rule (BofA marble family).
 *  - corner: minimal corner marks only, micro label (plain-stock and
 *            reduced backs: counter, money order, traveler's).
 */
export type WashBoxStyle = 'plain' | 'header' | 'double' | 'tinted' | 'corner';

export function washBoxStyleForVariant(variant: string): WashBoxStyle {
    switch (variant) {
        case 'V2': return 'header';
        case 'V3':
        case 'V10': return 'double';
        case 'V8':
        case 'V9': return 'tinted';
        case 'V4':
        case 'V5':
        case 'V6':
        case 'VT':
        case 'CAN':
        case 'V7L': return 'corner';
        case 'V1':
        default: return 'plain';
    }
}

export interface BackEngineConfigArgs {
    checkStyle: string;
    selectedBank: string | null | undefined;
    /** Security feature entries ({id, enabled}) from the studio state. */
    securityFeatures: Array<{ id: string; enabled: boolean }>;
    designName: string;
    designPrimary: string | null;
    designAccent: string | null;
    /** The EFFECTIVE deposit style (user override or variant default). */
    effectiveStyle: BackDepositStyle | string;
    accountNumber?: string;
    accountHolderName?: string;
    routingNumber?: string;
    checkNumber?: string;
}

export function buildBackEngineConfig(args: BackEngineConfigArgs): { config: any; depositConfig: any; resolved: ResolvedBackVariant } {
    const resolved = resolveBackVariant(args.checkStyle, args.selectedBank);
    const enabledIds = (args.securityFeatures || []).filter(f => f.enabled).map(f => f.id);
    const specFeatures = templateSpecFeaturesFor(args.checkStyle, args.selectedBank);
    const panelLines = panelLinesFor(resolved, enabledIds, specFeatures);

    // V10 inheritance: frame in the design's primary, inner rule in its
    // accent (§5.2). Named variants carry their own frame treatments.
    let frameStroke = resolved.frameStroke;
    let frameInner = resolved.frameInner;
    if (resolved.variant === 'V10') {
        frameStroke = args.designPrimary || frameStroke;
        frameInner = args.designAccent || frameInner;
    }

    const config: any = {
        style: args.effectiveStyle,
        bankName: args.designName,
        tint: args.designPrimary,
        tintAccent: args.designAccent,
        variant: resolved.variant,
        variantName: resolved.variantName,
        panelMode: resolved.panelMode,
        panelLines,
        panelExtended: resolved.panelExtended,
        footnote: resolved.footnote,
        watermarkText: resolved.watermarkText,
        fieldFill: resolved.fieldFill,
        stripFill: resolved.stripFill,
        frameStroke,
        frameInner,
        stripNote: resolved.stripNote,
        // Chemical Wash Detection Box (Phase 2): the back panel
        // composition gains the blank white wash box exactly when
        // the design carries chemical-sensitive paper — the feature
        // whose only furniture this is (the front renders none).
        chemicalWash: enabledIds.includes('chemical_reactive'),
        // The wash box's per-design style (null when the design has
        // no chemical-sensitive paper — then no box renders at all,
        // in-panel or anywhere; Princewill, 2026-11-04).
        washBoxStyle: enabledIds.includes('chemical_reactive')
            ? washBoxStyleForVariant(resolved.variant)
            : null,
        instructions: resolved.instructions,
        treasuryMicroprint: resolved.treasuryMicroprint,
        // ImageMatch payload. Default content is routing + check,
        // gated on the routing number (unchanged for every other
        // variant). 'account-check' variants (USAA) print account +
        // check instead, gated on either number existing — each
        // segment prints only when its number is present, and the
        // payload is null when both are blank.
        imageMatch: !resolved.imageMatch
            ? null
            : resolved.imageMatchContent === 'account-check'
                ? (args.accountNumber || args.checkNumber)
                    ? { accountNumber: args.accountNumber || '', checkNumber: args.checkNumber || '', content: 'account-check' }
                    : null
                : args.routingNumber
                    ? { routingNumber: args.routingNumber, checkNumber: args.checkNumber || '' }
                    : null
    };

    const depositConfig: any = {
        style: args.effectiveStyle,
        accountNumber: args.accountNumber,
        // The business deposit block prints the account-holder/business
        // name above FOR DEPOSIT ONLY (stamp order, spec C5) — only for
        // the business block, and only when a name is on the check.
        businessName: args.effectiveStyle === 'business' ? (args.accountHolderName || undefined) : undefined
    };

    return { config, depositConfig, resolved };
}
